import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Search, Upload, X, Image as ImageIcon, Loader2, Settings, Shapes } from 'lucide-react';
import { useAssetStore } from '../../stores/assetStore';
import type { AssetRecord, AssetType } from '../../types';
import AssetFolderList from './asset/AssetFolderList';
import AssetTile from './asset/AssetTile';
import AssetUploadQueue from './asset/AssetUploadQueue';
import AssetCompressionSettings from './asset/AssetCompressionSettings';

interface AssetLibraryPanelProps {
  onClose: () => void;
  onInsertAsset?: (asset: AssetRecord) => void;
}

const TYPE_OPTIONS: { id: AssetType | null; label: string }[] = [
  { id: null, label: 'All' },
  { id: 'image', label: 'Images' },
  { id: 'svg', label: 'SVGs' },
  { id: 'icon', label: 'Icons' },
];

export default function AssetLibraryPanel({ onClose, onInsertAsset }: AssetLibraryPanelProps) {
  const {
    assets,
    isLoading,
    error,
    searchQuery,
    typeFilter,
    activeFolderId,
    favoritesOnly,
    loadAssets,
    loadFolders,
    refreshUserRole,
    uploadMany,
    setSearchQuery,
    setTypeFilter,
    clearError,
  } = useAssetStore();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    loadAssets();
    loadFolders();
    refreshUserRole();
  }, [loadAssets, loadFolders, refreshUserRole]);

  const filteredAssets = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return assets.filter((a) => {
      if (favoritesOnly && !a.is_favorite) return false;
      if (!favoritesOnly && activeFolderId && a.folder_id !== activeFolderId) return false;
      if (typeFilter && a.asset_type !== typeFilter) return false;
      if (q) {
        const hay = `${a.name} ${a.file_name} ${(a.tags || []).join(' ')}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [assets, searchQuery, typeFilter, activeFolderId, favoritesOnly]);

  const handleUpload = useCallback(
    async (files: FileList | File[]) => {
      const arr = Array.from(files).filter(
        (f) => f.type.startsWith('image/') || /\.svg$/i.test(f.name)
      );
      if (arr.length === 0) return;
      await uploadMany(arr, activeFolderId);
    },
    [uploadMany, activeFolderId]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        handleUpload(e.dataTransfer.files);
      }
    },
    [handleUpload]
  );

  return (
    <div className="flex flex-col h-full bg-[#111] border-l border-white/[0.06] relative">
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-white/[0.06]">
        <div className="flex items-center gap-2">
          <Shapes size={14} className="text-cyan-400" />
          <h3 className="text-xs font-semibold text-zinc-200 uppercase tracking-wider">
            Assets
          </h3>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowSettings(true)}
            className="p-1 rounded hover:bg-white/[0.06] text-zinc-500 hover:text-zinc-200 transition-colors"
            title="Compression settings"
          >
            <Settings size={13} />
          </button>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/[0.06] text-zinc-500 hover:text-zinc-200 transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      <div className="px-3 py-2 space-y-2 border-b border-white/[0.06]">
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search assets..."
            className="w-full pl-7 pr-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-xs text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-cyan-500/30"
          />
        </div>

        <div className="flex gap-1">
          {TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.id ?? 'all'}
              onClick={() => setTypeFilter(opt.id)}
              className={`flex-1 px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                typeFilter === opt.id
                  ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30'
                  : 'bg-white/[0.03] text-zinc-500 border border-white/[0.06] hover:bg-white/[0.06]'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <AssetFolderList />

      {error && (
        <div className="mx-3 mt-2 p-2 rounded bg-red-500/10 border border-red-500/20 text-red-400 text-[10px] flex items-center justify-between">
          <span className="truncate">{error}</span>
          <button onClick={clearError} className="text-red-400/60 hover:text-red-400 shrink-0">
            <X size={12} />
          </button>
        </div>
      )}

      <div
        className={`flex-1 overflow-y-auto sidebar-scroll p-2.5 relative ${
          dragOver ? 'bg-cyan-500/5' : ''
        }`}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes('Files')) {
            e.preventDefault();
            setDragOver(true);
          }
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 size={18} className="animate-spin text-zinc-600" />
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-zinc-600">
            <ImageIcon size={26} className="mb-2 opacity-40" />
            <p className="text-xs text-zinc-500">
              {searchQuery ? 'No assets match' : 'No assets here yet'}
            </p>
            <p className="text-[10px] text-zinc-700 mt-1 text-center px-6">
              Drop images or SVGs here, or click "Upload"
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {filteredAssets.map((asset) => (
              <AssetTile
                key={asset.id}
                asset={asset}
                onInsert={(a) => onInsertAsset?.(a)}
              />
            ))}
          </div>
        )}

        {dragOver && (
          <div className="absolute inset-2.5 flex items-center justify-center bg-cyan-500/5 border-2 border-dashed border-cyan-500/40 rounded-lg pointer-events-none">
            <div className="text-center">
              <Upload size={20} className="mx-auto mb-1 text-cyan-400" />
              <p className="text-xs text-cyan-400 font-medium">Drop to upload</p>
              <p className="text-[10px] text-cyan-500/60 mt-0.5">
                Large files will be compressed
              </p>
            </div>
          </div>
        )}
      </div>

      <AssetUploadQueue />

      <div className="px-3 py-2 border-t border-white/[0.06]">
        <button
          onClick={() => fileInputRef.current?.click()}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-500/10 text-cyan-400 text-xs font-medium hover:bg-cyan-500/20 transition-colors"
        >
          <Upload size={14} />
          Upload Assets
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) handleUpload(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {showSettings && <AssetCompressionSettings onClose={() => setShowSettings(false)} />}
    </div>
  );
}
